import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import RegraClienteBooleanConfig from './RegraClienteBooleanConfig';
import MensagensContatoConfig from './MensagensContatoConfig';
import EmailsClienteConfig from './EmailsClienteConfig';
import SincronizarAlocacoesButton from './SincronizarAlocacoesButton';
import type { ConfigRegrasCliente } from '../../../models';

interface ConfiguracoesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Abre direto na tela dessa configuração, sem passar pelo menu — ex.: a ficha de Ativos
   * abre "Equipamento do Cliente" quando o Cliente proprietário ainda não está lá. */
  itemInicial?: (typeof ITENS_MENU)[number]['campo'];
}

/** Cada entrada vira um item no menu e, ao clicar, abre RegraClienteBooleanConfig
 * configurada pra `campo`. Menu de opções de configuração do SI — o painel é o lugar
 * único pra configurações que forem entrando depois (evita virar hardcode espalhado). */
const ITENS_MENU: Array<{
  campo: keyof Pick<ConfigRegrasCliente, 'permiteEquipamentoCliente' | 'usaEquipamentoHomologado'>;
  label: string;
  descricaoMenu: string;
  descricaoTela: string;
  /** Ver RegraClienteBooleanConfig — só usado por "Equipamento do Cliente". */
  mostrarEmpresaProprietaria?: boolean;
}> = [
  {
    campo: 'permiteEquipamentoCliente',
    label: 'Equipamento do Cliente',
    descricaoMenu: 'Quais Clientes permitem usar equipamento do próprio cliente.',
    descricaoTela:
      'Clientes selecionados aqui ganham a opção "Propriedade do Equipamento" nas Solicitações — com o nome do próprio cliente no lugar de "Raro". Preencha também o nome/CNPJ da empresa, usados no Termo gerado. Os demais Clientes nunca mostram esse campo (equipamento é sempre Raro).',
    mostrarEmpresaProprietaria: true,
  },
  {
    campo: 'usaEquipamentoHomologado',
    label: 'Equipamento Homologado',
    descricaoMenu: 'Quais Clientes usam máquina homologada.',
    descricaoTela: 'Clientes selecionados aqui têm colaboradores que utilizam equipamento homologado.',
  },
];

/**
 * Painel de configurações do SI — exclusivo de si_admin (mesmo padrão de drawer já usado
 * em SolicitacaoDrawer/NovaSolicitacaoDrawer). Estrutura de 2 níveis: um menu de opções
 * (aqui) e, ao clicar, a tela específica daquela configuração (ver RegraClienteBooleanConfig)
 * — pensado pra crescer sem virar uma única tela lotada conforme mais configurações entrem.
 */
export default function ConfiguracoesDrawer({ isOpen, onClose, itemInicial }: ConfiguracoesDrawerProps) {
  const [itemAberto, setItemAberto] = useState<(typeof ITENS_MENU)[number] | null>(null);
  // "Mensagens de contato" e "E-mails para o Cliente" têm tela própria (não são regra
  // booleana por Cliente) — painel mais largo.
  const [telaPropria, setTelaPropria] = useState<'mensagens' | 'emails' | null>(null);

  useEffect(() => {
    if (isOpen && itemInicial) setItemAberto(ITENS_MENU.find((item) => item.campo === itemInicial) ?? null);
  }, [isOpen, itemInicial]);

  const handleClose = () => {
    onClose();
    setItemAberto(null);
    setTelaPropria(null);
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={handleClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              className={`flex h-full w-full flex-col overflow-hidden bg-white shadow-xl transition-[max-width] ${
                telaPropria ? 'max-w-4xl' : 'max-w-lg'
              }`}
            >
              <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-4">
                <Dialog.Title className="font-montserrat text-lg font-semibold text-gray-900">
                  Configurações
                </Dialog.Title>
                <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose}>
                  <span className="sr-only">Fechar</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-6">
                {telaPropria === 'mensagens' ? (
                  <MensagensContatoConfig onVoltar={() => setTelaPropria(null)} />
                ) : telaPropria === 'emails' ? (
                  <EmailsClienteConfig onVoltar={() => setTelaPropria(null)} />
                ) :!itemAberto ? (
                  <div className="space-y-2">
                    {ITENS_MENU.map((item) => (
                      <button
                        key={item.campo}
                        type="button"
                        onClick={() => setItemAberto(item)}
                        className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                      >
                        <div>
                          <p className="font-montserrat font-medium text-gray-900">{item.label}</p>
                          <p className="mt-0.5 text-sm text-gray-500">{item.descricaoMenu}</p>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" />
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setTelaPropria('mensagens')}
                      className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-montserrat font-medium text-gray-900">Mensagens de contato</p>
                        <p className="mt-0.5 text-sm text-gray-500">Textos do WhatsApp/Teams e documentos que vão junto.</p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setTelaPropria('emails')}
                      className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-montserrat font-medium text-gray-900">E-mails para o Cliente</p>
                        <p className="mt-0.5 text-sm text-gray-500">Pedido de acesso/máquina e aviso de saída (ex.: SWAP).</p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" />
                    </button>
                    <SincronizarAlocacoesButton />
                  </div>
                ) : (
                  <RegraClienteBooleanConfig
                    campo={itemAberto.campo}
                    titulo={itemAberto.label}
                    descricao={itemAberto.descricaoTela}
                    mostrarEmpresaProprietaria={itemAberto.mostrarEmpresaProprietaria}
                    onVoltar={() => setItemAberto(null)}
                  />
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
